/**
 * Vague 18 — Cartographie 360° + Phantom Detector
 *
 * Page de vue x-ray agrégée pour un sujet (opportunité / entité / risque).
 * Route : /app/cartographie/:type/:id
 *
 * Affiche en couches superposables :
 *  • Sujet + position fractale verticale (5 niveaux)
 *  • Relations horizontales (annuaire, utility actors, chains)
 *  • Frictions et bottlenecks
 *  • Plans d'action liés
 *  • Doctrine active de référence
 *  • Phantom Detector : ce qui DEVRAIT être là et ne l'est pas
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Eye, EyeOff, Loader2, AlertTriangle, Ghost, Layers, Network,
  Flame, ListChecks, ScrollText, ArrowLeft, Sparkles, ChevronRight,
  Building2, User, Shield, ArrowUpRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { startLLMJob } from "@/components/LLMJobsTray";
import ModuleHelpButton from "@/components/ModuleHelpButton";
import Cartography360PDFButton from "@/components/Cartography360PDFButton";

const SEVERITY_COLOR = {
  critique: "#EF4444",
  majeur: "#EF9F27",
  mineur: "#F4D35E",
  info: "#7F77DD",
};

const SUBJECT_TYPE_META = {
  opportunite: { label: "Opportunité", icon: Sparkles, color: "#378ADD" },
  entite: { label: "Entité", icon: Building2, color: "#A78BFA" },
  risque: { label: "Risque", icon: Shield, color: "#EF4444" },
};

export default function Cartographie360() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);

  // Toggles des couches
  const [layers, setLayers] = useState({
    fractal: true,
    relations: true,
    frictions: true,
    plans: true,
    phantoms: true,
  });

  const load = () => {
    setLoading(true);
    setError("");
    api.get(`/workspace/cartographie/${type}/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(fmtError(e, "Cartographie indisponible")))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, id]);

  const runPhantomScan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/workspace/cartographie/phantoms/scan", {
        subject_type: type,
        subject_id: id,
      });
      setData((prev) => ({ ...prev, phantom_scan: r.data }));
      notify.success(`${r.data.phantoms_count} fantôme(s) détecté(s).`);
    } catch (e) {
      // En cas de 502 / timeout / quota, on garde l'éventuel scan précédent affiché
      const msg = fmtError(e, "Scan échoué");
      if (data?.phantom_scan) {
        notify.error(`${msg} — le scan précédent reste affiché.`);
      } else {
        notify.error(msg);
      }
    } finally {
      setScanning(false);
    }
  };

  const runPhantomScanBackground = async () => {
    try {
      await startLLMJob("phantom_scan", { subject_type: type, subject_id: id });
      notify.success("Scan lancé en arrière-plan. Vous serez notifié(e) au résultat.");
    } catch (e) {
      notify.error(fmtError(e, "Impossible de lancer le job"));
    }
  };

  const subjectMeta = SUBJECT_TYPE_META[type];
  if (!subjectMeta) {
    return (
      <div className="p-8 text-red-400">Type sujet invalide : {type}</div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 text-zinc-500" data-testid="carto360-loading">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement de la cartographie…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-red-400 space-y-3" data-testid="carto360-error">
        <div>{error}</div>
        <button onClick={() => navigate(-1)} className="text-xs underline text-zinc-400">Retour</button>
      </div>
    );
  }

  if (!data) return null;

  const SubjectIcon = subjectMeta.icon;

  return (
    <div className="space-y-6 p-1" data-testid="carto360-page">
      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 hover:bg-white/10 rounded text-zinc-400 flex-shrink-0"
          data-testid="carto360-back"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 noam-overline" style={{ color: subjectMeta.color }}>
            <Layers className="w-3 h-3" /> Cartographie 360° · {subjectMeta.label}
          </div>
          <h1 className="noam-display text-2xl mt-1 truncate" data-testid="carto360-subject-label">
            {data.subject.label}
          </h1>
          <p className="text-zinc-400 text-xs mt-1 max-w-3xl">
            Vue x-ray multi-couches : position fractale, relations, frictions, plans, fantômes.
            Toggle les couches pour focaliser la lecture.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Cartography360PDFButton data={data} filename={`noam-cartographie-${type}`} />
          <ModuleHelpButton moduleKey="cartographie360" />
          <SubjectIcon className="w-5 h-5" style={{ color: subjectMeta.color }} />
        </div>
      </div>

      {/* Layer toggles */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="carto360-layer-toggles">
        {[
          { key: "fractal", label: "Fractale", icon: Layers },
          { key: "relations", label: "Relations", icon: Network },
          { key: "frictions", label: "Frictions", icon: Flame },
          { key: "plans", label: "Plans", icon: ListChecks },
          { key: "phantoms", label: "Fantômes", icon: Ghost },
        ].map(({ key, label, icon: Icon }) => {
          const on = layers[key];
          return (
            <button
              key={key}
              onClick={() => setLayers((p) => ({ ...p, [key]: !p[key] }))}
              className={`text-[10px] px-2 py-1 rounded border inline-flex items-center gap-1 transition ${
                on ? "border-[#7F77DD]/50 bg-[#7F77DD]/10 text-[#7F77DD]" : "border-white/10 text-zinc-500 hover:text-zinc-300"
              }`}
              data-testid={`carto360-toggle-${key}`}
            >
              {on ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              <Icon className="w-3 h-3" />
              {label}
            </button>
          );
        })}
      </div>

      {/* Sujet — résumé */}
      <SubjectCard subject={data.subject} doctrine={data.doctrine} />

      {/* Phantom layer en tête (le silence prime) */}
      {layers.phantoms && (
        <PhantomLayer
          scan={data.phantom_scan}
          scanning={scanning}
          onScan={runPhantomScan}
          onScanBackground={runPhantomScanBackground}
        />
      )}

      {/* Layer Fractale verticale */}
      {layers.fractal && <FractalLayer fractal={data.fractal_position} />}

      {/* Layer Relations horizontales */}
      {layers.relations && <RelationsLayer relations={data.relations} />}

      {/* Layer Frictions */}
      {layers.frictions && <FrictionsLayer frictions={data.frictions} />}

      {/* Layer Plans */}
      {layers.plans && <PlansLayer plans={data.plans} />}
    </div>
  );
}

// ============================================================================
// Sujet — résumé
// ============================================================================
function SubjectCard({ subject, doctrine }) {
  const lines = [];
  if (subject.secteur) lines.push({ k: "Secteur", v: subject.secteur });
  if (subject.ticket_mEUR) lines.push({ k: "Ticket", v: `${subject.ticket_mEUR} M€` });
  if (subject.etape) lines.push({ k: "Étape", v: subject.etape });
  if (subject.owner) lines.push({ k: "Owner", v: subject.owner });
  if (subject.country) lines.push({ k: "Pays", v: subject.country });
  if (subject.status) lines.push({ k: "Statut", v: subject.status });
  if (subject.niveau) lines.push({ k: "Niveau", v: subject.niveau });
  if (subject.impact_net != null) lines.push({ k: "Impact net", v: subject.impact_net });
  if (subject.probabilite_nette != null) lines.push({ k: "Prob. nette", v: subject.probabilite_nette });
  if (subject.cout_inaction) lines.push({ k: "Coût inaction", v: `${subject.cout_inaction} k€` });
  if (subject.prob_closing != null) lines.push({ k: "Prob. closing", v: `${subject.prob_closing}%` });

  return (
    <div className="noam-card p-4" data-testid="carto360-subject-card">
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
        {lines.length === 0 ? (
          <span className="text-zinc-500">Aucune donnée structurée pour ce sujet.</span>
        ) : lines.map((l) => (
          <div key={l.k} className="flex items-baseline gap-1.5">
            <span className="text-zinc-500 text-[10px] uppercase tracking-wider">{l.k}</span>
            <span className="text-zinc-200">{l.v}</span>
          </div>
        ))}
      </div>
      {subject.description && (
        <p className="text-xs text-zinc-400 mt-3 border-t border-white/5 pt-3">{subject.description}</p>
      )}
      {doctrine && (
        <div className="text-[10px] text-zinc-500 mt-3 flex items-center gap-1">
          <ScrollText className="w-3 h-3" />
          Doctrine de référence : <span className="text-[#7F77DD]">{doctrine.title} v{doctrine.version}</span>
          <span className="text-zinc-600">· {doctrine.principles_count} principes</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Phantom Layer — la couche "silence"
// ============================================================================
function PhantomLayer({ scan, scanning, onScan, onScanBackground }) {
  return (
    <section
      className="noam-card p-4 border-l-2 border-l-[#A78BFA]/50"
      data-testid="carto360-phantom-layer"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="flex items-center gap-2 noam-overline text-[#A78BFA]">
            <Ghost className="w-3 h-3" /> Couche fantômes
          </div>
          <h2 className="text-base font-semibold text-zinc-100 mt-1">Le silence parlant</h2>
          <p className="text-[11px] text-zinc-500 mt-1 max-w-2xl">
            Ce qui <em>devrait</em> apparaître pour ce sujet et qui n'apparaît pas — NOAM interroge
            la doctrine pour détecter ressources pivots, gouvernance, réseau, timing manquants.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {onScanBackground && (
            <button
              onClick={onScanBackground}
              disabled={scanning}
              className="text-[11px] px-3 py-1.5 rounded border border-[#7F77DD]/40 bg-[#7F77DD]/10 text-[#7F77DD] hover:bg-[#7F77DD]/20 disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="carto360-phantom-scan-bg"
              title="Scan en arrière-plan — vous pouvez continuer à naviguer"
            >
              <Sparkles className="w-3 h-3" />
              Arrière-plan
            </button>
          )}
          <button
            onClick={onScan}
            disabled={scanning}
            className="text-[11px] px-3 py-1.5 rounded bg-[#A78BFA] hover:bg-[#A78BFA]/90 text-white disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="carto360-phantom-scan"
          >
            {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {scan ? "Re-scanner" : "Scanner les fantômes"}
          </button>
        </div>
      </div>

      {!scan && !scanning && (
        <div className="text-xs text-zinc-500 py-6 text-center border border-dashed border-white/10 rounded">
          Aucun scan effectué. Lancez le scan pour détecter les fantômes.
        </div>
      )}

      {scan && (
        <div className="space-y-3" data-testid="carto360-phantom-results">
          {/* Score + synthèse */}
          <div className="flex items-start gap-4">
            <div
              className="w-16 h-16 rounded-full border-4 flex items-center justify-center flex-shrink-0"
              style={{ borderColor: SEVERITY_COLOR[scan.phantom_score >= 70 ? "critique" : scan.phantom_score >= 40 ? "majeur" : "mineur"] }}
            >
              <span className="text-lg font-bold" style={{ color: SEVERITY_COLOR[scan.phantom_score >= 70 ? "critique" : scan.phantom_score >= 40 ? "majeur" : "mineur"] }}>
                {scan.phantom_score}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Phantom score · 0-100</div>
              <div className="text-xs text-zinc-200 mt-1">{scan.synthese}</div>
              {scan.total_cost_monthly_meur > 0 && (
                <div className="text-[10px] text-red-300 mt-1">
                  Coût d'inaction estimé : <strong>{scan.total_cost_monthly_meur.toFixed(2)} M€/mois</strong>
                </div>
              )}
              <div className="text-[10px] text-zinc-600 mt-1">
                Scanné {new Date(scan.scanned_at).toLocaleString("fr-CH")}
              </div>
            </div>
          </div>

          {/* Liste des fantômes */}
          {scan.phantoms && scan.phantoms.length > 0 && (
            <div className="space-y-2 mt-3" data-testid="carto360-phantom-list">
              {scan.phantoms.map((p, i) => (
                <div
                  key={i}
                  className="border border-white/10 rounded p-3 bg-white/[0.02]"
                  style={{ borderLeftColor: SEVERITY_COLOR[p.severity] || "#7F77DD", borderLeftWidth: 2 }}
                  data-testid={`carto360-phantom-${i}`}
                >
                  <div className="flex items-start gap-2">
                    <Ghost className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: SEVERITY_COLOR[p.severity] }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[9px] px-1.5 py-0.5 rounded font-bold bg-white/[0.04] text-zinc-400">
                          {p.rule_n ? `#${p.rule_n}` : "·"}
                        </span>
                        <span className="text-sm text-zinc-100 font-medium">{p.label}</span>
                        <span
                          className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-bold"
                          style={{ background: `${SEVERITY_COLOR[p.severity]}1A`, color: SEVERITY_COLOR[p.severity] }}
                        >
                          {p.severity}
                        </span>
                        {(p.type_label || p.type) && (
                          <span className="text-[9px] text-zinc-500 uppercase tracking-wider">
                            {p.type_label || p.type}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-400 mt-1.5">
                        <strong className="text-zinc-300">Attendu : </strong>{p.expected_what}
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-1">
                        <strong className="text-zinc-400">Observé : </strong>{p.current_state}
                      </div>
                      {p.suggested_action && (
                        <div className="text-[11px] text-[#1D9E75] mt-1.5 flex items-start gap-1">
                          <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span>{p.suggested_action}</span>
                        </div>
                      )}
                      {p.cost_inaction_meur_monthly > 0 && (
                        <div className="text-[10px] text-red-300 mt-1">
                          Coût inaction : {p.cost_inaction_meur_monthly.toFixed(2)} M€/mois
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ============================================================================
// Fractal Layer — verticale 5 niveaux
// ============================================================================
function FractalLayer({ fractal }) {
  if (!fractal) return null;
  const completeness = fractal.completeness_pct || 0;
  return (
    <section className="noam-card p-4" data-testid="carto360-fractal-layer">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 noam-overline text-[#7F77DD]">
            <Layers className="w-3 h-3" /> Couche fractale verticale
          </div>
          <h2 className="text-base font-semibold text-zinc-100 mt-1">Position dans la fractale</h2>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Complétude</div>
          <div className="text-base font-bold" style={{ color: completeness >= 80 ? "#1D9E75" : completeness >= 50 ? "#EF9F27" : "#EF4444" }}>
            {completeness}%
          </div>
        </div>
      </div>
      <div className="space-y-1.5">
        {(fractal.levels || []).slice().reverse().map((lvl) => (
          <div
            key={lvl.n}
            className={`flex items-center gap-3 p-2 rounded border ${
              lvl.filled ? "border-[#7F77DD]/30 bg-[#7F77DD]/5" : "border-white/5 bg-white/[0.02]"
            }`}
            data-testid={`carto360-fractal-level-${lvl.n}`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
              lvl.filled ? "bg-[#7F77DD]/20 text-[#7F77DD]" : "bg-white/5 text-zinc-600"
            }`}>
              N{lvl.n}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{lvl.title}</div>
              <div className={`text-sm truncate ${lvl.filled ? "text-zinc-200" : "text-zinc-600 italic"}`}>
                {lvl.value || "— non renseigné —"}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================================
// Relations Layer — horizontale
// ============================================================================
function RelationsLayer({ relations }) {
  if (!relations) return null;
  const annu = relations.annuaire || [];
  const utils = relations.utility_actors || [];
  const chains = relations.chains || [];
  const empty = annu.length + utils.length + chains.length === 0;
  return (
    <section className="noam-card p-4" data-testid="carto360-relations-layer">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 noam-overline text-[#378ADD]">
            <Network className="w-3 h-3" /> Couche relations horizontales
          </div>
          <h2 className="text-base font-semibold text-zinc-100 mt-1">Réseau identifié ({relations.total || 0})</h2>
        </div>
      </div>
      {empty ? (
        <div className="text-xs text-zinc-500 py-4 text-center">Aucune relation identifiée — fantôme potentiel à scanner.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <RelationBlock title="Annuaire" items={annu} color="#378ADD" testid="carto360-relations-annuaire" render={(e) => (
            <Link to={`/app/cartographie/entite/${e.id}`} className="hover:underline text-zinc-200 truncate flex items-center gap-1">
              {e.name} <ArrowUpRight className="w-3 h-3 text-zinc-500" />
            </Link>
          )} subtitle={(e) => e.relation || e.type || "—"} />
          <RelationBlock title="Utility actors" items={utils} color="#A78BFA" testid="carto360-relations-utility" render={(a) => (
            <span className="text-zinc-200 truncate">{a.name}</span>
          )} subtitle={(a) => `${a.role || a.type} · ${a.criticite}`} />
          <RelationBlock title="Chaînages causals" items={chains} color="#EF9F27" testid="carto360-relations-chains" render={(c) => (
            <span className="text-zinc-200 truncate">{c.title}</span>
          )} subtitle={(c) => c.description?.slice(0, 60) || c.relation || "—"} />
        </div>
      )}
    </section>
  );
}

function RelationBlock({ title, items, color, render, subtitle, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color }}>
        {title} ({items.length})
      </div>
      <div className="space-y-1">
        {items.length === 0 ? (
          <div className="text-[10px] text-zinc-600 italic">Aucun</div>
        ) : items.slice(0, 8).map((it, i) => (
          <div key={it.id || i} className="text-[11px] p-2 bg-white/[0.02] border border-white/5 rounded">
            <div className="flex items-center gap-1">{render(it)}</div>
            <div className="text-[10px] text-zinc-500 mt-0.5">{subtitle(it)}</div>
          </div>
        ))}
        {items.length > 8 && (
          <div className="text-[10px] text-zinc-600 text-center pt-1">… et {items.length - 8} de plus</div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Frictions Layer
// ============================================================================
function FrictionsLayer({ frictions }) {
  if (!frictions) return null;
  const items = frictions.items || [];
  return (
    <section className="noam-card p-4" data-testid="carto360-frictions-layer">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2 noam-overline text-[#EF9F27]">
            <Flame className="w-3 h-3" /> Couche frictions
          </div>
          <h2 className="text-base font-semibold text-zinc-100 mt-1">
            {frictions.items_count} friction(s) · {frictions.snapshots_count} analyse(s)
          </h2>
        </div>
        {frictions.total_cost_monthly_meur > 0 && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Coût d'inaction</div>
            <div className="text-base font-bold text-red-300">
              {frictions.total_cost_monthly_meur.toFixed(2)} M€/mois
            </div>
            <div className="text-[10px] text-zinc-500">{frictions.total_cost_annual_meur?.toFixed(1)} M€/an</div>
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-zinc-500 py-4 text-center">
          Aucune friction analysée pour ce sujet — lancez une analyse Friction Gap depuis Origination.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.slice(0, 8).map((f, i) => (
            <div
              key={i}
              className="text-[11px] flex items-start gap-2 p-2 bg-white/[0.02] border border-white/5 rounded"
              data-testid={`carto360-friction-${i}`}
            >
              <span className="text-[#EF9F27] font-bold w-6 text-center flex-shrink-0">{f.score_friction || "?"}</span>
              <div className="flex-1 min-w-0">
                <div className="text-zinc-200">{f.label}</div>
                {f.comment_lever && (
                  <div className="text-zinc-500 text-[10px] mt-0.5">→ {f.comment_lever}</div>
                )}
              </div>
              <span className="text-[10px] text-zinc-500 flex-shrink-0">{f.categorie}</span>
            </div>
          ))}
          {items.length > 8 && (
            <div className="text-[10px] text-zinc-600 text-center pt-1">… et {items.length - 8} de plus</div>
          )}
        </div>
      )}
    </section>
  );
}

// ============================================================================
// Plans Layer
// ============================================================================
function PlansLayer({ plans }) {
  if (!plans) return null;
  const items = plans.items || [];
  return (
    <section className="noam-card p-4" data-testid="carto360-plans-layer">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2 noam-overline text-[#1D9E75]">
            <ListChecks className="w-3 h-3" /> Couche plans d'action
          </div>
          <h2 className="text-base font-semibold text-zinc-100 mt-1">
            {plans.items_count} plan(s) · {plans.open_count} ouvert(s) · {plans.late_count} en retard
          </h2>
        </div>
        <Link
          to="/app/plans"
          className="text-[11px] text-[#7F77DD] hover:underline flex items-center gap-1"
          data-testid="carto360-plans-link"
        >
          Module Plans <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-zinc-500 py-4 text-center">
          Aucun plan associé — si le coût d'inaction est élevé, c'est un fantôme.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.slice(0, 6).map((p) => (
            <div
              key={p.id}
              className="text-[11px] flex items-center gap-2 p-2 bg-white/[0.02] border border-white/5 rounded"
              data-testid={`carto360-plan-${p.id}`}
            >
              <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{
                background: p.priorite === "P0" ? "#EF44441A" : p.priorite === "P1" ? "#EF9F271A" : "#A1A1AA1A",
                color: p.priorite === "P0" ? "#EF4444" : p.priorite === "P1" ? "#EF9F27" : "#A1A1AA",
              }}>{p.priorite}</span>
              <span className="text-zinc-200 flex-1 truncate">{p.titre}</span>
              <span className="text-zinc-500 text-[10px]">{p.statut}</span>
              <span className="text-zinc-400 text-[10px]">{p.progression || 0}%</span>
            </div>
          ))}
          {items.length > 6 && (
            <div className="text-[10px] text-zinc-600 text-center pt-1">… et {items.length - 6} de plus</div>
          )}
        </div>
      )}
    </section>
  );
}
